import { Injectable } from '@angular/core';
import { AlertService } from '../alert/alert.service';
import { LoadingService } from '../loading/loading.service';
import { DataService } from '../data.service';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, of, Observable, forkJoin } from 'rxjs';
import {
  Carrier,
  ExtendedStorage,
  HHGIffShipmentDetailResponse
} from 'src/app/shared/common-components/common-structure';
import {catchError, tap, mergeMap, shareReplay, switchMap, startWith, map} from 'rxjs/operators';
import { StateCountryService } from '../state-country/state-country.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class XTOSReportingService extends DataService {

  private baseURI = this.apiUri + '/hhgextstorage/';
  private XTOSReportingUserId = new BehaviorSubject<number>(0);
  allContiguousStates$ = this.stateCountryService.getAllStates().pipe(
    // map(states => states.filter(state => state.contiguousUS > 0)),
    shareReplay(1)
  );
  user$ = this.userService.getCurrentUserObservable().pipe(shareReplay(1));

  xtosShipmentDetailData$ = this.XTOSReportingUserId.pipe(
    tap(() => this.loader.show()),
    mergeMap(id => forkJoin([this.getXTOSCarrierList(id), this.getXTOSShipmentList(id), this.allContiguousStates$])),
    tap(([carrierList, xtosShipmentList, states]) => {
      carrierList.sort((a, b) => a.scac.localeCompare(b.scac));
      xtosShipmentList.forEach(xtos => {
        xtos.storageStateAbbrev = states.find(state => state.stateCountryId === xtos.storageStateId)
          ? states.find(state => state.stateCountryId === xtos.storageStateId).stateCountryCode : '';
        xtos.scac = carrierList.find(carrier => carrier.carrierId === xtos.carrierId) ?
          carrierList.find(carrier => carrier.carrierId === xtos.carrierId).scac : '';
      });
      states.forEach((state, index) => {
        if (state.stateCountryId === 1773 || state.stateCountryId === 1294 ||
          state.stateCountryId === 368 || state.stateCountryId === 1296) {
          states.splice(index, 1);
        }
      });
      this.loader.hide();
    })
  );

  constructor(http: HttpClient, private stateCountryService: StateCountryService,
    private alertService: AlertService, private loader: LoadingService, private userService: UserService) {
    super(http);
    this.setUri(this.baseURI);
  }

  updateXTOSReportingUserId(userId: number) {
    this.XTOSReportingUserId.next(userId);
  }

  getXTOSReportingMasterSummary(): Observable<any> {
    return this.http.get(this.apiUri + '/xtosMasterList');
  }

  getXTOSReportingMasterDetailSummary(xtosMasterId: any): Observable<any> {
    return this.http.get(this.apiUri + '/xtosMasterList/get/' + xtosMasterId + '/masterid');
  }

  getXTOSCarrierList(userId: number): Observable<Carrier[]> {
    const uri = this.baseURI + 'getAllScacByuser';
    this.setUri(uri);
    return this.get().pipe(catchError((e) => {
      return of([] as Carrier[]);
    }));
  }

  getXTOSShipmentList(userId: number): Observable<ExtendedStorage[]> {
    const uri = this.baseURI + 'getAllByUser';
    this.setUri(uri);
    return this.get().pipe(catchError((e) => {
      return of([] as ExtendedStorage[]);
    }));
  }

  saveXTOSShipment(XTOSShipment: ExtendedStorage, userId: number) : Observable<any>{
    const uri = this.baseURI + 'save';
    this.setUri(uri);
    this.loader.show();
    if(XTOSShipment === null) return;
    return this.update(XTOSShipment)
      .pipe(
        tap(s => {
          this.updateXTOSReportingUserId(userId);
          this.loader.hide();
          this.alertService.success('Successfully saved the xtos shipment');
        }),
        map(() => true), 
        catchError((e) => {
          this.loader.hide();
           this.alertService.error((e.error &&  typeof e?.error === 'string') ? e.error : 'Error saving the xtos shipment');
          return of(XTOSShipment);
        }));
  }

  deleteXTOSShipment(XTOSShipment: ExtendedStorage, userId: number) {
    const uri = this.baseURI;
    this.setUri(uri);
    this.loader.show();
    return this.deleteWithPut(XTOSShipment.hhgExtStorageShipmentId)
      .pipe(
        tap(s => {
          this.updateXTOSReportingUserId(userId);
          this.loader.hide();
          this.alertService.success('Successfully deleted the xtos shipment');
        })
        , catchError((e) => {
          this.loader.hide();
          this.alertService.error('Error deleting the xtos shipment');
          return of({} as ExtendedStorage);
        }));
  }

  deleteXtosShipment(xtosMasterIds: number[]): Observable<any> {
    this.setUri(this.apiUri + '/xtosMasterList/deletextosshipment');
    return this.update(xtosMasterIds);
  }

  getXTOSComprehensiveReport(reportingYear: number, reportingQuarter: number): Observable<any> {
    const iffDetailsUrl = this.apiUri + '/xtosShipmentSummary' + '/xtosComprehensive/' + reportingYear + '/reportingyear/' + reportingQuarter + '/reportingQuarter';
    this.setUri(iffDetailsUrl);
    return this.get();
  }

  /**
   * @description gets the list of agencies
   */
  getXtosSroMaster(): Observable<any> {
    return this.http.get(this.apiUri + '/ratessromaster/srotype/Extended Storage/businesstype/HHG');
  }
}
